import type { ManagedAs, MyStageRow, MyWork, OwnedTaskRow } from "@fieldpath/shared";
import { FINISHED_RECENT_DAYS, mdy, mdyWeekday } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate } from "../router";
import { todayLocal } from "./DateHint";
import { FinishedRow } from "./FinishedWork";
import { HelpButton } from "./HelpButton";
import { ManagedCalendarDialog, MyCalendarDialog } from "./Calendar";
import { KindTag } from "./KindTag";
import { NewPersonalTask } from "./NewPersonalTask";
import { openInstructionsOnArrival } from "./openInstructions";
import { useTaskHover } from "./TaskHoverCard";
import { summary } from "../workSummary";
import type { TaskHoverBind } from "./TaskHoverCard";

interface Props {
  fetcher: Fetcher;
  onOpenTask: (taskId: string) => void;
  /**
   * Deltek flags this person PM or admin. `null` while unknown or after a
   * failed check, which is not evidence either way.
   */
  managesWork: boolean | null;
}

/** How a date reads to someone scanning: "2 days late" beats "09/24/2026". */
function dueLabel(days: number | null, date: string | null) {
  if (date === null) return { text: "no date", tone: "muted" as const };
  if (days === null) return { text: mdy(date), tone: "muted" as const };
  if (days < 0) {
    const n = Math.abs(days);
    return { text: `${n} day${n === 1 ? "" : "s"} late`, tone: "overdue" as const };
  }
  if (days === 0) return { text: "today", tone: "overdue" as const };
  if (days === 1) return { text: "tomorrow", tone: "soon" as const };
  if (days <= 5) return { text: `in ${days} days`, tone: "soon" as const };
  return { text: mdy(date), tone: "muted" as const };
}

/**
 * When upcoming work should reach you: the date on the stage the task is at
 * now, as Workload's "arrives ~" (22 Oct 2026). Past means the stage before
 * yours is late, which is said, not hidden.
 *
 * Punch list F168 (30 Oct 2026): "arrives 11/04/2026", "was due 10/28/2026"
 * or "no date yet" -- the date as every date reads (MM/DD/YYYY, F193), no
 * "tomorrow" or "in 3 days".
 * Past or not is still the server's `daysUntilArrival`, so "today" is the
 * same day it was before.
 */
export function arrivalLabel(days: number | null, date: string | null): { verb: string; when: string | null } {
  if (date === null || days === null) return { verb: "no date yet", when: null };
  const when = mdy(date);
  return days < 0 ? { verb: "was due", when } : { verb: "arrives", when };
}

/**
 * The four bands of the ledger, in the order a day actually runs.
 *
 * **This replaced three columns and four counter tiles on 4 Oct 2026.** The
 * columns answered three different questions — what do I do now, what am I
 * accountable for, what is coming — and the reason they were side by side was
 * that stacked vertically the second and third only exist if you scroll, which
 * on a busy day nobody does.
 *
 * That reasoning still holds, and the ledger honours it differently: what is
 * ON you is one list sorted by when it bites, and what you MANAGE moved to a
 * panel beside it rather than below it. Nothing went under a fold.
 *
 * The tiles went because they were four numbers you could already count. A
 * tile saying "1 blocked" next to a list containing that one blocked thing is
 * a summary of a thing already on screen.
 */
type Band = "late" | "week" | "later" | "coming";

const BAND_LABEL: Record<Band, string> = {
  late: "Late & due today",
  week: "This week",
  later: "Due later",
  coming: "Coming to you",
};

function bandFor(row: MyStageRow): Band {
  const days = row.daysUntilStageDue;
  // Undated work is LATER, not late. It has not slipped; nobody has said when
  // it is due, and sorting it with the overdue would cry wolf every morning.
  if (days === null) return "later";
  if (days <= 0) return "late";
  if (days <= 7) return "week";
  return "later";
}

export function Home({ fetcher, onOpenTask, managesWork }: Props) {
  // One hook per screen. `bind` goes on each row; `card` is a portal, so
  // where it is rendered does not matter, only that it is.
  const { bind, card } = useTaskHover(fetcher);
  const [work, setWork] = useState<MyWork | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which calendar is open over the page (C.1), if either.
  const [calendar, setCalendar] = useState<"mine" | "managed" | null>(null);
  // "Done in the last 30 days" starts folded (punch list F112): it is a record, not a to-do.
  const [doneOpen, setDoneOpen] = useState(false);
  // New personal task (punch list F171): its small form, open or not.
  const [addingPersonal, setAddingPersonal] = useState(false);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void api
      .myWork(fetcher)
      .then(setWork)
      .catch((e: Error) => setError(e.message));
  }, [fetcher, back]);

  if (error) return <div className="error">{error}</div>;
  if (!work) return <p className="muted">Loading…</p>;

  const overdue = work.current.filter(
    (w) => w.daysUntilStageDue !== null && w.daysUntilStageDue < 0,
  ).length;
  const blocked = work.current.filter((w) => w.isBlocked).length;
  const firstName = work.name?.split(" ")[0] ?? "there";

  /*
   * Grouped once, in order, so the bands keep the order the API returned
   * WITHIN each band. The API already sorts by urgency; re-sorting here would
   * mean two places decide what "urgent" means and they would drift.
   */
  const bands: { band: Band; rows: MyStageRow[]; upcoming: boolean }[] = [
    { band: "late" as const, rows: work.current.filter((r) => bandFor(r) === "late"), upcoming: false },
    { band: "week" as const, rows: work.current.filter((r) => bandFor(r) === "week"), upcoming: false },
    { band: "later" as const, rows: work.current.filter((r) => bandFor(r) === "later"), upcoming: false },
    { band: "coming" as const, rows: [...work.upcoming], upcoming: true },
  ].filter((g) => g.rows.length > 0);
  const yours = bands.filter((g) => !g.upcoming);
  const coming = bands.filter((g) => g.upcoming);

  /*
   * "You manage" only where it can hold anything (punch list #11, 12 Oct
   * 2026): somebody flagged PM or admin, OR anybody who does manage a task
   * here -- a project or deliverable PM/TM needs no flag, and hiding work
   * they own would be worse than an empty panel. Everyone else gets the
   * full width for their own work instead of a column that is always blank.
   */
  const showManaged = managesWork === true || work.owned.length > 0;

  return (
    <div className={showManaged ? "ledger-wrap" : "ledger-wrap solo"}>
      {card}
      {calendar === "mine" ? (
        <MyCalendarDialog fetcher={fetcher} onClose={() => setCalendar(null)} />
      ) : calendar === "managed" ? (
        <ManagedCalendarDialog fetcher={fetcher} onClose={() => setCalendar(null)} />
      ) : null}
      <div className="ledger-main">
        <div className="home-top">
          <div className="today-line">{todayLine()}</div>
          <div className="home-top-actions">
            {/* Toggles to Cancel, as New task does on a project. */}
            <button type="button" className="cal-open-button" onClick={() => setAddingPersonal((a) => !a)}>
              {addingPersonal ? "Cancel" : "New personal task"}
            </button>
            <button type="button" className="cal-open-button" onClick={() => setCalendar("mine")}>
              <CalendarIcon />
              My calendar
            </button>
          </div>
        </div>
        <h1 className="greeting">
          Good {partOfDay()}, {firstName}.
          <HelpButton screens="home" />
        </h1>
        <p className="greeting-sub">{summary(work.current.length, overdue, blocked)}</p>

        {addingPersonal ? (
          <NewPersonalTask
            fetcher={fetcher}
            onCreated={(taskId) => {
              // Straight to the new task, its instructions open to type into
              // (Jeremy, 1 Nov 2026, F171).
              openInstructionsOnArrival(taskId);
              navigate({ kind: "task", id: taskId });
            }}
          />
        ) : null}

        {bands.length === 0 ? (
          <div className="empty" style={{ marginTop: 24 }}>
            Nothing needs you at the moment.
          </div>
        ) : (
          <>
            {/* Punch list F168: the three bands of work in hand sit under one
                heading, so "Coming to you" reads as not yet yours. */}
            {yours.length > 0 ? (
              <section className="ledger-group">
                <div className="section-label">
                  <span>Yours to do</span>
                </div>
                {yours.map(({ band, rows }) => (
                  <div key={band} className="ledger-subgroup">
                    <div className="section-label sub">
                      <span>{BAND_LABEL[band]}</span>
                      <span className="count">{rows.length}</span>
                    </div>
                    {rows.map((row) => (
                      <LedgerRow
                        key={row.taskId + row.stageName}
                        row={row}
                        upcoming={false}
                        onOpen={onOpenTask}
                        hover={bind}
                      />
                    ))}
                  </div>
                ))}
              </section>
            ) : null}
            {coming.map(({ band, rows }) => (
              <section key={band} className="ledger-group">
                <div className="section-label">
                  <span>{BAND_LABEL[band]}</span>
                  <span className="count">{rows.length}</span>
                </div>
                {rows.map((row) => (
                  <LedgerRow
                    key={row.taskId + row.stageName}
                    row={row}
                    upcoming
                    onOpen={onOpenTask}
                    hover={bind}
                  />
                ))}
              </section>
            ))}
          </>
        )}

        {/* What you finished lately (punch list F112, 30 Oct 2026), folded at the
            bottom so it never pushes today's work down, and the way to the
            Finished work page for anything older (F117). */}
        <section className="ledger-group ledger-done">
          <div className="section-label">
            <button
              type="button"
              className="done-toggle"
              aria-expanded={doneOpen}
              onClick={() => setDoneOpen((v) => !v)}
            >
              <span className="done-caret" aria-hidden="true">{doneOpen ? "▾" : "▸"}</span>
              Done in the last {FINISHED_RECENT_DAYS} days
            </button>
            <span className="count">{work.done.length}</span>
          </div>
          {doneOpen ? (
            work.done.length === 0 ? (
              <div className="empty done-empty">
                You have not finished a stage in the last {FINISHED_RECENT_DAYS} days.
              </div>
            ) : (
              work.done.map((row) => (
                <FinishedRow
                  key={row.taskId + row.stageName + row.completedAt}
                  row={row}
                  onOpen={onOpenTask}
                  hover={bind}
                />
              ))
            )
          ) : null}
          <button type="button" className="linkish done-more" onClick={() => navigate({ kind: "finished" })}>
            Search everything you have finished
          </button>
        </section>
      </div>

      {showManaged ? (
      <aside className="ledger-side">
        <div className="section-label">
          <span>You manage</span>
          <button
            type="button"
            className="cal-open-button small"
            onClick={() => setCalendar("managed")}
          >
            <CalendarIcon />
            Calendar
          </button>
        </div>
        {work.owned.length === 0 ? (
          <div className="empty">Nothing open is yours to manage.</div>
        ) : (
          work.owned.map((t) => (
            <OwnedItem key={t.taskId} row={t} onOpen={onOpenTask} hover={bind} />
          ))
        )}
        {/* The handoff's note here read "Hover a task to preview it; click to
            open it." The hover preview is Phase 4, which was skipped, so the
            first half would have been a lie about a feature that does not
            exist. */}
        <p className="side-note">Click a task to open it.</p>
      </aside>
      ) : null}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" />
    </svg>
  );
}

/** "MON 11/02/2026" — the date, stated once, so no row has to carry it. */
function todayLine(): string {
  return mdyWeekday(todayLocal()).toUpperCase();
}

function partOfDay(): string {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}

function LedgerRow({
  row,
  upcoming,
  onOpen,
  hover,
}: {
  row: MyStageRow;
  upcoming: boolean;
  onOpen: (taskId: string) => void;
  hover: TaskHoverBind;
}) {
  const due = dueLabel(row.daysUntilStageDue, row.stageDueDate);
  const arrival = upcoming ? arrivalLabel(row.daysUntilArrival, row.expectedArrival) : null;

  return (
    <button
      className={`ledger-row${upcoming ? " is-upcoming" : ""}`}
      onClick={() => onOpen(row.taskId)}
      {...hover(row.taskId, row.taskTitle)}
    >
      {/* When it bites, first and in its own column, so the eye runs down one
          edge rather than hunting across each row. Upcoming work says when it
          ARRIVES: a date somebody cannot act on yet is not what they need. */}
      <span className={`ledger-due tone-${upcoming ? "muted" : due.tone}`}>
        {/* The "when" never breaks: in this 116px column "arrives 2026-11-04"
            wrapped inside the date. The verb takes the first line instead. */}
        {arrival ? (
          arrival.when === null ? (
            arrival.verb
          ) : (
            <>
              {arrival.verb} <span className="ledger-when">{arrival.when}</span>
            </>
          )
        ) : (
          due.text
        )}
      </span>

      <span className="ledger-mid">
        <span className="ledger-title">
          <KindTag kind="task" size="row" />
          {row.taskTitle}
        </span>
        <Where
          projectName={row.projectName}
          deliverableName={row.deliverableName}
          clientName={row.clientName}
        />
      </span>

      <span className="ledger-right">
        {row.isBlocked ? <span className="blocked-badge">BLOCKED</span> : null}
        <span className="stage-box">{row.stageName}</span>
      </span>
    </button>
  );
}

const MANAGED_AS_LABEL: Record<Exclude<ManagedAs, "owner">, string> = {
  deliverable_pm: "DLV PM",
  deliverable_tm: "DLV TM",
  project_pm: "PRJ PM",
  project_tm: "PRJ TM",
};

/** The first letters of a name, for the small round avatar. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return (words[0]![0]! + (words[1]?.[0] ?? "")).toUpperCase();
}

function OwnedItem({
  row,
  onOpen,
  hover,
}: {
  row: OwnedTaskRow;
  onOpen: (taskId: string) => void;
  hover: TaskHoverBind;
}) {
  const holder = row.withWhom ?? "nobody";

  return (
    <button
      className="owned-item"
      onClick={() => onOpen(row.taskId)}
      {...hover(row.taskId, row.taskTitle)}
    >
      <span className="owned-title">
        <KindTag kind="task" size="row" />
        <span className="owned-name">{row.taskTitle}</span>
        {row.stageOverdue ? <span className="owned-late">LATE</span> : null}
        {/* Why it is here, when it is not yours outright. A task you do not
            own turning up in your list needs its reason on the row, or it
            reads as a mistake (E9.3). */}
        {row.managedAs !== "owner" ? (
          <span className="owned-as">{MANAGED_AS_LABEL[row.managedAs]}</span>
        ) : null}
      </span>
      <Where
        projectName={row.projectName}
        deliverableName={row.deliverableName}
        clientName={row.clientName}
      />
      {/* The question a PM actually asks, and the one Wrike cannot answer:
          not who was assigned originally, but who has it right now. */}
      <span className="owned-holder">
        <span className="avatar" aria-hidden="true">
          {row.withWhom ? initials(row.withWhom) : "—"}
        </span>
        <span className="owned-who">{holder}</span>
        {row.currentStage ? (
          <span className="muted"> · {row.currentStage}</span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * Where a piece of work sits: project, then deliverable if it has one.
 *
 * Carries the same PRJ / DLV / TSK tags as everywhere else, so the dashboard
 * reads with the same vocabulary as the project screen rather than being its
 * own dialect. The deliverable is the useful addition — "which client
 * commitment is this part of" is the question the project name alone cannot
 * answer.
 */
function Where({
  projectName,
  deliverableName,
  clientName,
}: {
  projectName: string;
  deliverableName: string | null;
  clientName: string | null;
}) {
  return (
    <span className="work-where">
      <KindTag kind="project" size="crumb" />
      {projectName}
      {deliverableName ? (
        <>
          <span className="sep"> › </span>
          <KindTag kind="deliverable" size="crumb" />
          <span className="deliv-name">{deliverableName}</span>
        </>
      ) : null}
      {clientName ? (
        <span className="muted">
          {" · "}
          <span className="client-name">{clientName}</span>
        </span>
      ) : null}
    </span>
  );
}
